import { useEffect, useState } from "react";
import { Gauge, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  DASHBOARD_DEFAULT_PHASE_COLORS,
  DASHBOARD_DEFAULT_TILE_COLORS,
  loadDashboardPreferences,
  saveDashboardPreferences,
} from "../dashboardAppearancePreferences";

const SUMMARY_CARDS = [
  { id: "all_active", label: "All Active" },
  { id: "in_review", label: "In Review" },
  { id: "pending_graphics", label: "Pending Graphics" },
  { id: "ready_for_submission", label: "Graphics Prepress" },
];

const PHASES = [
  { id: "awaiting_graphics_approval", label: "Awaiting Graphics Approval" },
  { id: "ready_for_submission", label: "Graphics Prepress" },
  { id: "artwork_submitted", label: "Artwork Submitted" },
  { id: "pdf_proof", label: "PDF Proof" },
  { id: "swatch", label: "Swatch" },
  { id: "pilot", label: "Pilot" },
  { id: "graphics_complete", label: "Graphics Complete" },
];

export const DashboardAppearanceSettings = () => {
  const [prefs, setPrefs] = useState(loadDashboardPreferences);

  useEffect(() => {
    saveDashboardPreferences(prefs);
  }, [prefs]);

  const reset = () => {
    const defaults = {
      phaseColorSource: "kanban",
      tileColors: { ...DASHBOARD_DEFAULT_TILE_COLORS },
      phaseColors: { ...DASHBOARD_DEFAULT_PHASE_COLORS },
    };
    setPrefs(defaults);
    toast.success("Dashboard appearance reset to defaults");
  };

  return (
    <section className="mt-8 max-w-4xl bg-card border border-border rounded-xl p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Gauge size={20} />
            Dashboard Appearance
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Control Dashboard summary-card accents and workflow phase colors.
          </p>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 px-3 py-2 bg-secondary rounded-lg text-sm"
        >
          <RotateCcw size={15} />
          Reset Defaults
        </button>
      </div>

      <div className="space-y-7">
        <div>
          <div className="mb-3">
            <h3 className="text-sm font-medium">Summary Card Colors</h3>
            <p className="text-xs text-muted-foreground mt-1">
              These colors control the accent line, number, and icon treatment on the four Dashboard summary cards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {SUMMARY_CARDS.map((card) => (
              <label
                key={card.id}
                className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3"
              >
                <input
                  type="color"
                  value={prefs.tileColors[card.id] || DASHBOARD_DEFAULT_TILE_COLORS[card.id]}
                  onChange={(event) => setPrefs((previous) => ({
                    ...previous,
                    tileColors: {
                      ...previous.tileColors,
                      [card.id]: event.target.value,
                    },
                  }))}
                  className="h-9 w-12 border-0 bg-transparent p-0 cursor-pointer"
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{card.label}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {prefs.tileColors[card.id] || DASHBOARD_DEFAULT_TILE_COLORS[card.id]}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Workflow Phase Color Source</label>
          <select
            value={prefs.phaseColorSource}
            onChange={(event) => setPrefs((previous) => ({
              ...previous,
              phaseColorSource: event.target.value,
            }))}
            className="w-full max-w-md px-4 py-2.5 bg-background border border-border rounded-lg"
          >
            <option value="kanban">Follow Kanban category colors</option>
            <option value="custom">Use separate Dashboard colors</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Following Kanban keeps both views visually consistent. Choose separate colors to tune the Dashboard independently.
          </p>
        </div>

        {prefs.phaseColorSource === "custom" && (
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-medium">Dashboard Workflow Phase Colors</h3>
              <p className="text-xs text-muted-foreground mt-1">
                These colors control the Workflow Phases area and related workload counts on the Dashboard only.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {PHASES.map((phase) => (
                <label
                  key={phase.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3"
                >
                  <input
                    type="color"
                    value={prefs.phaseColors[phase.id] || DASHBOARD_DEFAULT_PHASE_COLORS[phase.id]}
                    onChange={(event) => setPrefs((previous) => ({
                      ...previous,
                      phaseColors: {
                        ...previous.phaseColors,
                        [phase.id]: event.target.value,
                      },
                    }))}
                    className="h-9 w-12 border-0 bg-transparent p-0 cursor-pointer"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{phase.label}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {prefs.phaseColors[phase.id] || DASHBOARD_DEFAULT_PHASE_COLORS[phase.id]}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
